import Panel from "components/Panel";
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { WorkbookDetailsDocument } from "../Workbook/gql/Query.generated";
import { useUpdateWorkbookParameterSetsMutation } from "./gql/Mutation.update.generated";
import {
  WorkbookType,
  WorkbookVariantType,
  AliasParamMapType,
} from "../Workbook";

import {
  convertParamValue,
  ParameterSetsType,
  ParamSetType,
  stringifyValue,
} from "../Workbook/util";
import Callout from "components/Callout";
import {
  faCheckCircle,
  faCog,
  faExclamationCircle,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import { useNavigate, useParams } from "react-router-dom";
import { useRoutes } from "utils/routes";
import { VariantPanel } from "../WorkbookVariant";
import PanelSection from "components/PanelSection";
import PageContent from "components/PageContent";
import {
  CreateVariantSteps,
  VariantCreationHeaderWithVariant,
} from "../RewriteQuery";
import Button from "components/Button";
import EditParameterSettingsPanel from "../EditParameterSettingsPanel";

const EditParameterSets = ({
  workbook,
  variant,
}: {
  workbook: WorkbookType;
  variant: WorkbookVariantType;
}) => {
  const { databaseId } = useParams();
  const [errorMessage, setErrorMessage] = useState("");
  const [showParameterSettingsEditPanel, setShowParameterSettingsEditPanel] =
    useState(false);
  const { databaseWorkbookVariantRun } = useRoutes();
  const navigate = useNavigate();
  // key = setId, value = param set (params to newly add)
  const [newParameterSets, setNewParameterSets] = useState<ParameterSetsType>(
    {},
  );

  const [updateWorkbookParameterSets] = useUpdateWorkbookParameterSetsMutation({
    refetchQueries: [
      {
        query: WorkbookDetailsDocument,
        variables: { workbookId: workbook.id, databaseId },
      },
    ],
  });

  const expectedAliasesLength = workbook.parameterRefAliases.length;
  // For the case the workbook was initially created without params but new param set is added
  const newParamNotFilled =
    workbook.parameterSets.length === 0 && expectedAliasesLength > 0;
  const missingAliasCount = newParamNotFilled
    ? expectedAliasesLength
    : expectedAliasesLength - workbook.parameterSets[0].paramValues.length;
  const allValuesSet =
    !newParamNotFilled &&
    Object.keys(newParameterSets).length === workbook.parameterSets.length &&
    Object.values(newParameterSets).every(
      (paramSet) => Object.keys(paramSet).length === missingAliasCount,
    );

  const handleRunExplain = () => {
    if (!allValuesSet) {
      setErrorMessage(
        "Please enter values, types, or NULL flags for all parameters.",
      );
      return;
    }
    const convertedParameterSets = Object.fromEntries(
      Object.entries(newParameterSets).map(([setId, paramSet]) => {
        const newSet = Object.entries(paramSet).map(([pkey, pvalue]) => {
          return [
            pkey,
            {
              value: convertParamValue(pvalue),
            },
          ];
        });
        return [setId, Object.fromEntries(newSet)];
      }),
    );
    updateWorkbookParameterSets({
      variables: {
        workbookId: workbook.id,
        parameterSets: convertedParameterSets,
      },
      onCompleted: () => {
        navigate(
          databaseWorkbookVariantRun(
            workbook.databaseId,
            workbook.id,
            variant.id,
          ),
        );
      },
      onError: (error) => {
        setErrorMessage(error.message);
      },
    });
  };
  const title = <VariantCreationHeaderWithVariant variant={variant} />;
  const editParameterSettings = (
    <Button
      bare
      onClick={() => setShowParameterSettingsEditPanel(true)}
      variant="link"
    >
      <FontAwesomeIcon icon={faCog} className="mr-0.5" /> Edit parameter
      settings
    </Button>
  );

  // TODO: show component to allow creating a new param set when newParamNotFilled
  return (
    <PageContent
      windowTitle={`EXPLAIN Workbook: ${workbook.name}`}
      title={title}
      pageCategory="workbooks"
      pageName="edit-parameter-sets"
    >
      <Panel
        title={<CreateVariantSteps step="step1" />}
        secondaryTitle={editParameterSettings}
      >
        <PanelSection className="grid gap-2">
          <VariantPanel workbook={workbook} variant={variant} />
          <Callout title="Parameter Sets need an update" variant="warning">
            New parameters are added with this new variant. Please update
            parameter set values.
          </Callout>
          <div>
            {workbook.aliasParamMapList.map((val) => {
              return (
                <PanelSection key={val.id}>
                  <ParameterSetCell
                    currParameterSet={val}
                    workbook={workbook}
                    setNewParameterSets={setNewParameterSets}
                  />
                </PanelSection>
              );
            })}
          </div>
          {errorMessage && (
            <div className="text-[#C22426]">
              <FontAwesomeIcon icon={faExclamationCircle} /> {errorMessage}
            </div>
          )}
          <div>
            <button
              className="btn btn-success !px-4"
              disabled={!allValuesSet}
              onClick={handleRunExplain}
            >
              Run EXPLAIN...
            </button>
          </div>
        </PanelSection>
      </Panel>
      {showParameterSettingsEditPanel && (
        <EditParameterSettingsPanel
          onDismiss={() => setShowParameterSettingsEditPanel(false)}
          workbook={workbook}
          showExistingParameterWarning
        />
      )}
    </PageContent>
  );
};

const ParameterSetCell = ({
  currParameterSet,
  workbook,
  setNewParameterSets,
}: {
  currParameterSet: AliasParamMapType;
  workbook: WorkbookType;
  setNewParameterSets: React.Dispatch<React.SetStateAction<ParameterSetsType>>;
}) => {
  const [paramSet, setParamSet] = useState<ParamSetType>({});

  const missingAliases = missingValueAliases(workbook, currParameterSet.id);

  // TODO: lots of same functions exist in CreateParameterSetPanel, combine them
  const handleParamValuesChange = (alias: string, value: string) => {
    const prevParam = paramSet[alias];
    const newSet = { ...paramSet };
    if (value === "" && !prevParam?.type && !prevParam?.isNull) {
      // remove from the set when the value becomes an empty string without type or flagged as null
      delete newSet[alias];
    } else {
      newSet[alias] = {
        value: value,
        type: prevParam?.type,
        isNull: prevParam?.isNull,
      };
    }
    setParamSet(newSet);
    setNewParameterSets((prevState) => {
      return {
        ...prevState,
        [currParameterSet.id]: newSet,
      };
    });
  };
  const handleParamNullChange = (alias: string, isNull: boolean) => {
    const prevParam = paramSet[alias];
    const newSet = { ...paramSet };
    // when null is checked but there is an existing value, empty it
    newSet[alias] = {
      value: isNull ? undefined : prevParam?.value,
      type: prevParam?.type,
      isNull: isNull,
    };
    setParamSet(newSet);
    setNewParameterSets((prevState) => {
      return {
        ...prevState,
        [currParameterSet.id]: newSet,
      };
    });
  };

  const inputFields: React.JSX.Element[] = [];
  missingAliases.forEach((alias) => {
    inputFields.push(
      <div key={`label-${alias}`} className="font-medium">
        ${alias}
      </div>,
    );
    inputFields.push(
      <input
        key={`input-${alias}`}
        className="bg-white rounded border border-gray-300 box-content h-5 leading-5 px-2 py-1.5 disabled:bg-[#eee]"
        type="text"
        value={paramSet[alias]?.value ?? ""}
        disabled={paramSet[alias]?.isNull}
        onChange={(e) => {
          handleParamValuesChange(alias, e.target.value);
        }}
      />,
    );
    inputFields.push(
      <div key={`null-${alias}`}>
        NULL:{" "}
        <input
          type="checkbox"
          checked={paramSet[alias]?.isNull || false}
          onChange={(e) => handleParamNullChange(alias, e.target.checked)}
        />
      </div>,
    );
  });
  return (
    <div>
      <div>
        <div className="flex gap-2 items-center">
          {missingAliases.length === 0 ? (
            <FontAwesomeIcon icon={faCheckCircle} />
          ) : (
            <FontAwesomeIcon icon={faTriangleExclamation} />
          )}
          <div className="font-medium">{currParameterSet.name}</div>
        </div>
        <div>
          <pre className="border-none m-0 p-0 bg-none bg-transparent whitespace-pre-wrap">
            {Object.entries(currParameterSet.parameters as ParamSetType).map(
              ([key, value], i, arr) => {
                const missing = missingAliases.includes(key.slice(1));
                const missingClass = missing ? "text-[#A16006]" : "";
                return (
                  <React.Fragment key={key}>
                    <span className={missingClass}>
                      {missing
                        ? key
                        : `${key} = ${stringifyValue(value.value)}`}
                    </span>
                    {i + 1 < arr.length && ", "}
                  </React.Fragment>
                );
              },
            )}
          </pre>
        </div>
      </div>
      {missingAliases.length > 0 && (
        <div>
          <div className="grid grid-cols-[min-content_1fr_80px] gap-2 mb-2 mt-2 items-center">
            {Object.entries(inputFields).map(([_, elem]) => elem)}
          </div>
        </div>
      )}
    </div>
  );
};

export const missingValueAliases = (workbook: WorkbookType, setId: string) => {
  const existingParam = workbook.parameterSets.find((val) => val.id === setId);
  const expectedAliases = workbook.parameterRefAliases;
  return expectedAliases.slice(existingParam.paramValues.length);
};

export default EditParameterSets;
